import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { requestTyped } from '@xdocs/ws-protocol';
import { invoiceListQuery } from '@xdocs/schemas';
import type { ErrorAlert, Invoice, InvoiceListResult } from '@xdocs/schemas';
import type { DataTableQuery } from '@xdocs/ui';
import { useAuth } from '../../../auth/AuthProvider.js';
import { useWs } from '../../../ws/WsProvider.js';

const DEFAULT_QUERY: DataTableQuery = { page: 1, pageSize: 25, sort: 'issuedAt', dir: 'desc' };

export interface UseInvoicesResult {
  rows: Invoice[];
  total: number | null;
  nextCursor: string | null;
  query: DataTableQuery;
  onQueryChange: (next: DataTableQuery) => void;
  onLoadMore: () => void;
  isLoading: boolean;
  isLoadingMore: boolean;
  isError: boolean;
  loadMoreError: string | undefined;
  onRetry: () => void;
}

/** F26 invoices table — server-side pagination over `billing.invoices.list`
 * (5-min-TTL PG cache server-side per §E1), same `<DataTable>`-bridging
 * shape as `useSharesList`/`useCreditLedger`. */
export function useInvoices(enabledOverride: boolean): UseInvoicesResult {
  const { userId } = useAuth();
  const { wsClient, connectionState } = useWs();
  const [query, setQuery] = useState<DataTableQuery>(DEFAULT_QUERY);
  const [rows, setRows] = useState<Invoice[]>([]);
  const appendModeRef = useRef(false);
  const enabled = connectionState === 'connected' && userId !== null && enabledOverride;
  const queryKey = useMemo(() => ['user', userId, 'billing', 'invoices', query] as const, [userId, query]);

  const result = useQuery<InvoiceListResult, ErrorAlert>({
    queryKey,
    queryFn: () => requestTyped(wsClient, 'billing.invoices.list', invoiceListQuery.parse(query)),
    enabled,
  });

  useEffect(() => {
    if (!result.data) return;
    setRows((prev) => (appendModeRef.current ? [...prev, ...result.data.rows] : result.data.rows));
  }, [result.data]);

  const onQueryChange = useCallback((next: DataTableQuery) => {
    appendModeRef.current = false;
    setQuery(next);
  }, []);

  const onLoadMore = useCallback(() => {
    appendModeRef.current = true;
    setQuery((q) => ({ ...q, page: q.page + 1 }));
  }, []);

  const total = result.data?.total ?? null;
  const hasMore = total !== null ? rows.length < total : false;
  const isAppendFetch = appendModeRef.current && result.isFetching;

  return {
    rows,
    total,
    nextCursor: hasMore ? 'more' : null,
    query,
    onQueryChange,
    onLoadMore,
    isLoading: result.isLoading && !appendModeRef.current,
    isLoadingMore: isAppendFetch,
    isError: result.isError,
    loadMoreError: appendModeRef.current && result.isError ? result.error?.message : undefined,
    onRetry: () => void result.refetch(),
  };
}
